import React, { useState, useEffect, useRef } from "react";
import {
  useLogin,
  useNotify,
  useRefresh,
  usePermissions,
  useRedirect,
  useStore,
} from "react-admin";
import {
  Box,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  IconButton,
  OutlinedInput,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useForm } from "react-hook-form";
import { Loader } from "../../Loader";
import CryptoJS from "crypto-js";
import ReCAPTCHA from "react-google-recaptcha";
import Parse from "parse";
import SessionConflictModal from "./SessionConflictModal";

const SECRET_KEY = process.env.REACT_APP_SECRET_KEY;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const LoginPage = () => {
  const [userBalance, setUserBalance] = useStore("userBalance", 0);
  const { refetch } = usePermissions();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const login = useLogin();
  const notify = useNotify();
  const recaptchaRef = useRef();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({ mode: "onBlur" });

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaVerified, setCaptchaVerified] = useState(false);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingCredentials, setPendingCredentials] = useState(null);
  const [pendingUserId, setPendingUserId] = useState(null);

  useEffect(() => {
    const savedUsername = localStorage.getItem("rememberedUsername");
    const encryptedPassword = localStorage.getItem("rememberedPassword");
    if (savedUsername && encryptedPassword) {
      setValue("username", savedUsername);
      const bytes = CryptoJS.AES.decrypt(encryptedPassword, SECRET_KEY);
      const decryptedPassword = bytes.toString(CryptoJS.enc.Utf8);
      setValue("password", decryptedPassword);
      setRememberMe(true);
    }
  }, [setValue]);

  useEffect(() => {
    const currentUser = Parse.User.current();
    if (currentUser) {
      redirect("/");
    }
  }, [redirect]);

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const performLogin = async (username, password) => {
    try {
      const response = await login({ username, password });

      if (response?.userBalance !== undefined) {
        setUserBalance(response.userBalance);
      }

      if (rememberMe) {
        localStorage.setItem("rememberedUsername", username);
        const encryptedPassword = CryptoJS.AES.encrypt(
          password,
          SECRET_KEY
        ).toString();
        localStorage.setItem("rememberedPassword", encryptedPassword);
      } else {
        localStorage.removeItem("rememberedUsername");
        localStorage.removeItem("rememberedPassword");
      }

      await refetch();
      await refresh();
      setLoading(false);
      // redirect("/users");
      if (response?.role === "Super-User") {
        redirect("/dashboard");
      } else {
        redirect("/users");
      }
    } catch (error) {
      notify(error?.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    if (process.env.REACT_APP_ENV === "production" && !captchaToken) {
      notify("Please complete the CAPTCHA", { type: "warning" });
      return;
    }

    const { username, password } = data;
    setLoading(true);

    try {
      setCaptchaVerified(true);

      const sessionCheck = await Parse.Cloud.run("checkActiveSession", {
        username,
      });

      if (sessionCheck.activeSessions > 0) {
        setPendingCredentials({ username, password });
        setPendingUserId(sessionCheck.userId);
        setShowConfirmModal(true);
        setLoading(false);
        return;
      }

      await performLogin(username, password);
    } catch (error) {
      notify(error?.message || "Login failed. Please try again.");
      setLoading(false);
    }
  };

  const handleConfirmContinue = async () => {
    setLoading(true);

    try {
      await Parse.Cloud.run("logoutAllSessions", { userId: pendingUserId });
      await performLogin(
        pendingCredentials.username,
        pendingCredentials.password
      );
      setShowConfirmModal(false);
    } catch (error) {
      notify("Error clearing old sessions: " + error.message);
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      {loading && <Loader />}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh", // Ensure full height
          width: "100vw", // Ensure full width
          backgroundColor: "#000",
          backgroundImage: "url('/Assets/Images/login.svg')",
          backgroundSize: "cover", // Fully covers the screen
          backgroundPosition: "top",
          backgroundRepeat: "no-repeat",
          "@media (max-width: 600px)": {
            backgroundImage: "none", // Remove image on mobile
            backgroundColor: "#000", // Keep background color
          },
        }}
      >
        <Box
          component="form"
          noValidate
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            width: 400,
            p: 4,
            bgcolor: "#0E0F11",
            borderRadius: 2,
            boxShadow: 3,
          }}
        >
          <Typography htmlFor="username" sx={{ color: "white", mb: 1 }}>
            Username / Email
          </Typography>
          <OutlinedInput
            fullWidth
            placeholder="Enter Your  Username / Email"
            {...register("username", {
              required: " Username / Email is required",
            })}
            error={!!errors.username}
            sx={{ backgroundColor: "#222", color: "#fff" }}
          />
          {errors.username && (
            <FormHelperText sx={{ color: "#d32f2f" }}>
              {errors.username.message}
            </FormHelperText>
          )}

          <Typography htmlFor="password" sx={{ color: "white", mb: 1, mt: 2 }}>
            Password
          </Typography>
          <OutlinedInput
            fullWidth
            type={showPassword ? "text" : "password"}
            placeholder="Enter your password"
            {...register("password", {
              required: "Password is required",
              minLength: {
                value: 6,
                message: "Password must be at least 6 characters",
              },
            })}
            error={!!errors.password}
            endAdornment={
              <InputAdornment position="end">
                <IconButton onClick={handleTogglePassword} edge="end">
                  {showPassword ? (
                    <VisibilityOff sx={{ color: "#fff" }} />
                  ) : (
                    <Visibility sx={{ color: "#fff" }} />
                  )}
                </IconButton>
              </InputAdornment>
            }
            sx={{ backgroundColor: "#222", color: "#fff" }}
          />
          {errors.password && (
            <FormHelperText sx={{ color: "#d32f2f" }}>
              {errors.password.message}
            </FormHelperText>
          )}

          {process.env.REACT_APP_ENV === "production" && !captchaVerified && (
            <Box sx={{ mt: 2 }}>
              <div className="recaptcha-container">
                <ReCAPTCHA
                  sitekey={RECAPTCHA_SITE_KEY}
                  onChange={(token) => setCaptchaToken(token)}
                  onExpired={() => {
                    setCaptchaToken(null);
                    try {
                      recaptchaRef.current?.reset();
                    } catch (err) {
                      console.warn("ReCAPTCHA reset error", err);
                    }
                  }}
                  theme="dark"
                  ref={recaptchaRef}
                />
              </div>
            </Box>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                sx={{ color: "#FFC107" }}
              />
            }
            label={<Typography color="white">Remember Me</Typography>}
          />

          <Button
            fullWidth
            type="submit"
            variant="contained"
            sx={{
              bgcolor: "#FFC107",
              color: "#0E0F11",
              fontSize: "16px",
              "&:hover": { bgcolor: "#e6c300" },
            }}
          >
            Login
          </Button>
        </Box>
      </Box>
      <SessionConflictModal
        open={showConfirmModal}
        onConfirm={handleConfirmContinue}
        onCancel={() => setShowConfirmModal(false)}
      />
    </React.Fragment>
  );
};

export default LoginPage;
